import get from 'lodash/get'
import * as Sentry from '@sentry/react'
import { formatDate } from '../../../../van-shared/utils/utils'
import { isUntracked } from './utils'

// Flattens an alias map (e.g. {one: "GHSC-MAL0084A"} or {one: [...], epn: [...]})
// into a plain, upper-cased list of alias code strings.
const aliasValuesOf = (aliasMap) => {
  return Object.values(aliasMap || {})
    .flatMap(entry => (Array.isArray(entry) ? entry : [entry]))
    .filter(value => typeof value === 'string')
    .map(value => value.toUpperCase())
}

// A batch's productId can be an alias code rather than a real product _id
// (e.g. set from an SFTP/alias import). Aliases aren't just a case variant of
// the real _id - a generic parent product's `alias.<name>` array can list
// several manufacturer-specific codes (e.g. GHSC-MAL0084, GHSC-MAL0084A,
// GHSC-MAL0084B, ...) that all resolve to that one product doc, whose real
// _id may not resemble the alias string at all.
//
// The batch doc itself usually carries the authoritative alias it was
// imported under (masterDataBatch.alias), so prefer searching by that over
// guessing an alias from productId's string. Search against already-loaded
// products rather than issuing a new fetch, since we need this synchronously.
//
// When an alias matches more than one product, prefer the manufacturer-specific
// one - mirrors pickAliasedProduct in api/lib/product/api/read/get.js, which
// resolves this same ambiguity for the server's product.getByAlias.
const findProductByAlias = (productsById, aliasValues) => {
  if (!aliasValues.length) {
    return undefined
  }

  const matches = Object.values(productsById).filter(product =>
    aliasValuesOf(product.alias).some(alias => aliasValues.includes(alias))
  )
  if (!matches.length) {
    return undefined
  }
  if (matches.length === 1) {
    return matches[0]
  }

  const isManufacturerSpecific = doc =>
    doc.genericParent && doc.code && doc.genericParent.toUpperCase() !== doc.code.toUpperCase()
  const specificMatches = matches.filter(isManufacturerSpecific)
  const candidates = specificMatches.length ? specificMatches : matches
  const exactCodeMatch = candidates.find(doc => doc.code && aliasValues.includes(doc.code.toUpperCase()))
  return exactCodeMatch || candidates[0]
}

// A product can be missing while the local master-data pouch is still
// replicating. Callers already fall back to the batch's productId for display,
// so report it rather than crash the page. Selectors run on every render, so
// only report each product once.
const reportedMissingProductIds = new Set()
const reportMissingProduct = (productId, batchId) => {
  if (reportedMissingProductIds.has(productId)) {
    return
  }
  reportedMissingProductIds.add(productId)
  Sentry.withScope((scope) => {
    scope.setLevel('warning')
    scope.setTag('component', 'batch-selectors')
    scope.setExtra('productId', productId)
    scope.setExtra('batchId', batchId)
    Sentry.captureMessage('Product missing from master data in batchWithMasterData')
  })
}

export const batchWithMasterData = (state, batchId) => {
  const productId = state.batches[batchId].productId
  const masterDataBatch = state.masterData.batches[batchId]

  // Prefer the batch's own recorded alias; fall back to guessing one from
  // productId (e.g. "product:GHSC-MAL0084A" -> "GHSC-MAL0084A") for batches
  // that don't carry an alias field.
  const batchAliasValues = aliasValuesOf(get(masterDataBatch, 'alias'))
  const fallbackAliasValues = batchAliasValues.length
    ? batchAliasValues
    : [productId.replace(/^product:/i, '').toUpperCase()]

  const product = state.masterData.products.byId[productId] ||
    state.masterData.products.byId[productId.toLowerCase()] ||
    findProductByAlias(state.masterData.products.byId, fallbackAliasValues)
  if (!product) {
    reportMissingProduct(productId, batchId)
  }

  return {
    ...masterDataBatch,
    ...state.batches[batchId],
    formattedExpiry: formatDate(get(masterDataBatch, 'expiry'), 'expiry'),
    productName: get(product, 'name'),
    productCode: get(product, 'code'),
    productPresentation: get(product, 'presentation'),
    isUntracked: isUntracked(product)
  }
}

export const batchesByShipment = (state, snapshotId) => {
  return Object.keys(state.batches)
    .filter(batchId => {
      const batch = state.batches[batchId]
      return batch.snapshotId === snapshotId
    })
    .map(batchId => batchWithMasterData(state, batchId))
}
